import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

// Fully-editable "Why Antera" section. Everything (eyebrow, tri-part headline,
// intro, cards, trust bullets, CTA) comes from the CMS block config. Icons for
// the 4 cards stay as the hand-drawn SVG line-art the client shipped — the
// admin picks which icon to use per card via a select field.

/* ---------- Icon primitives ---------- */
const strokeProps = {
  fill: "none",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  strokeWidth: 2,
};

function GradDefs({ id }) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#3b82f6" />
        <stop offset="100%" stopColor="#1e3a8a" />
      </linearGradient>
    </defs>
  );
}

function IconWrap({ children, id, testId }) {
  return (
    <svg viewBox="0 0 64 64" width="100%" height="100%" role="img" data-testid={testId}>
      <GradDefs id={id} />
      <g stroke={`url(#${id})`} {...strokeProps}>
        {children}
      </g>
    </svg>
  );
}

function AdvancedTechIcon() {
  return (
    <IconWrap id="wa-tech" testId="why-antera-icon-tech">
      <path d="M18 42 Q10 42 10 34 Q10 26 20 26 Q22 16 34 16 Q46 16 48 26 Q58 26 58 34 Q58 42 50 42 Z" />
      <path d="M32 22 L26 34 L32 34 L28 46 L38 32 L32 32 Z" />
    </IconWrap>
  );
}

function InnovationIcon() {
  return (
    <IconWrap id="wa-inno" testId="why-antera-icon-innovation">
      <path d="M52 20 A20 20 0 1 0 54 40" />
      <path d="M46 12 L52 20 L44 22" />
      <path d="M12 44 A20 20 0 0 0 46 52" />
      <path d="M18 60 L12 44 L26 46" />
      <circle cx="32" cy="32" r="3" />
    </IconWrap>
  );
}

function AIIcon() {
  return (
    <IconWrap id="wa-ai" testId="why-antera-icon-ai">
      <rect x="18" y="18" width="28" height="28" rx="3" />
      <rect x="26" y="26" width="12" height="12" rx="1.5" />
      <line x1="18" y1="24" x2="12" y2="24" />
      <line x1="18" y1="32" x2="12" y2="32" />
      <line x1="18" y1="40" x2="12" y2="40" />
      <line x1="46" y1="24" x2="52" y2="24" />
      <line x1="46" y1="32" x2="52" y2="32" />
      <line x1="46" y1="40" x2="52" y2="40" />
      <line x1="24" y1="18" x2="24" y2="12" />
      <line x1="32" y1="18" x2="32" y2="12" />
      <line x1="40" y1="18" x2="40" y2="12" />
      <line x1="24" y1="46" x2="24" y2="52" />
      <line x1="32" y1="46" x2="32" y2="52" />
      <line x1="40" y1="46" x2="40" y2="52" />
      <circle cx="32" cy="32" r="1.5" />
    </IconWrap>
  );
}

function PresentationIcon() {
  return (
    <IconWrap id="wa-pres" testId="why-antera-icon-presentation">
      <rect x="8" y="14" width="48" height="32" rx="2" />
      <rect x="12" y="18" width="20" height="14" rx="1" />
      <circle cx="17" cy="23" r="1.5" />
      <path d="M13 30 L19 25 L24 28 L31 22" />
      <line x1="36" y1="20" x2="52" y2="20" />
      <line x1="36" y1="26" x2="52" y2="26" />
      <line x1="36" y1="32" x2="48" y2="32" />
      <line x1="32" y1="46" x2="32" y2="52" />
      <line x1="24" y1="52" x2="40" y2="52" />
    </IconWrap>
  );
}

const ICONS = {
  tech: AdvancedTechIcon,
  innovation: InnovationIcon,
  ai: AIIcon,
  presentation: PresentationIcon,
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

// Icon fallback by card position so pages that predate the `icon` field still
// render with reasonable defaults.
const DEFAULT_ICON_ORDER = ["tech", "innovation", "ai", "presentation"];

export default function WhyAnteraBlock({ data }) {
  const d = data || {};
  const eyebrow = d.eyebrow || "";
  const headingLead = d.heading_lead || "";
  const headingHighlight = d.heading_highlight || "";
  const intro = d.intro || "";
  const cards = Array.isArray(d.cards) ? d.cards.filter((c) => c && c.title) : [];
  const trustBullets = Array.isArray(d.trust_bullets)
    ? d.trust_bullets.filter((b) => b && b.text)
    : [];
  const ctaLabel = d.cta_label || "";
  const ctaHref = d.cta_href || "";

  return (
    <section
      data-testid="why-antera-section"
      className="py-16 lg:py-24 bg-slate-50/60 relative overflow-hidden"
    >
      <div className="absolute -top-20 -left-20 w-96 h-96 bg-sky-100/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -right-20 w-[28rem] h-[28rem] bg-indigo-100/40 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Heading */}
        {(eyebrow || headingLead || headingHighlight || intro) && (
          <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
            {eyebrow && (
              <span
                data-testid="why-antera-eyebrow"
                className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-sky-600"
              >
                {eyebrow}
              </span>
            )}
            {(headingLead || headingHighlight) && (
              <h2
                data-testid="why-antera-heading"
                className="mt-3 text-3xl sm:text-4xl lg:text-[2.6rem] font-extrabold tracking-tight text-slate-900 leading-[1.15]"
              >
                {headingLead}
                {headingHighlight && (
                  <>
                    {headingLead ? " " : ""}
                    <span className="bg-gradient-to-r from-sky-500 to-indigo-600 bg-clip-text text-transparent">
                      {headingHighlight}
                    </span>
                  </>
                )}
              </h2>
            )}
            {intro && (
              <p className="mt-5 text-base lg:text-lg text-slate-500 leading-relaxed">{intro}</p>
            )}
          </div>
        )}

        {/* Cards */}
        {cards.length > 0 && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6 max-w-5xl mx-auto"
          >
            {cards.map((card, i) => {
              const iconKey = card.icon || DEFAULT_ICON_ORDER[i] || "tech";
              const Icon = ICONS[iconKey] || AdvancedTechIcon;
              return (
                <motion.article
                  key={`${card.title}-${i}`}
                  variants={cardVariants}
                  data-testid={`why-antera-card-${card.title
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-|-$/g, "")}`}
                  className="group relative bg-white border border-slate-200 rounded-2xl p-6 sm:p-7 lg:p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-5">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 group-hover:scale-105 transition-transform duration-300">
                      <Icon />
                    </div>
                    <div className="min-w-0 w-full">
                      <h3 className="text-lg lg:text-xl font-bold text-slate-900 mb-2 leading-snug">
                        {card.title}
                      </h3>
                      <p className="text-sm lg:text-[15px] text-slate-500 leading-relaxed">
                        {card.body}
                      </p>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        )}

        {/* Trust bar + CTA */}
        {(trustBullets.length > 0 || ctaLabel) && (
          <div className="mt-14 lg:mt-16 max-w-5xl mx-auto">
            <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
            {trustBullets.length > 0 && (
              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                {trustBullets.map((b, i) => (
                  <div
                    key={`${b.text}-${i}`}
                    data-testid={`why-antera-trust-${b.text
                      .split(" ")[0]
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "")}`}
                    className="flex items-start gap-3"
                  >
                    <span className="w-6 h-6 shrink-0 rounded-full bg-sky-50 border border-sky-100 flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-sky-600" strokeWidth={2.5} />
                    </span>
                    <p className="text-sm lg:text-[15px] text-slate-700 font-medium leading-snug">
                      {b.text}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {ctaLabel && (
              <div className="mt-10 text-center">
                <Link to={ctaHref || "/"}>
                  <Button
                    data-testid="why-antera-cta-btn"
                    className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 h-12 text-sm font-semibold shadow-lg shadow-slate-900/10"
                  >
                    {ctaLabel} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
